import { OrderDetailsAPIExport } from "node-library/dist/apiDataFormats";
import {
	FrontendClient,
	FrontendCompanyTestCategory,
	FrontendOrder,
	FrontendOrganization,
	FrontendOrganizationLicense,
	FrontendUserInfo,
} from "node-library/dist/portal";
import React, { useState } from "react";
import { GreenButton } from "../../../common-components/button";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { useTranslation } from "react-i18next";
import { Card } from "@mui/material";
import CPDifferentInfoRow from "./cpDifferentInfoRow";
import {
	DataHandleOption,
	LicenseDesignation,
	Severity,
} from "node-library/dist/constants";
import { NeutralColor, SemanticColor } from "node-library/dist/colors";
import { UserIcon } from "../../../common-components/svgIcons";
import {
	findAssociatedClient,
	findAssociatedLicense,
	getDescriptionTextForHandleOption,
} from "../../../helpers/barcodeHelper";
import {
	CustomDropdown,
	StandardRadioGroup,
} from "../../../common-components/inputs";
import { ButtonTextLoadingAnimation } from "../../../common-components/animation/ButtonLoadingAnimation";
import { sendPOSTRequest } from "../../../requestHelper/sendRequest";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";

interface Props {
	testCategory: FrontendCompanyTestCategory;

	user: FrontendUserInfo | undefined;
	orderInfo: OrderDetailsAPIExport;
	associatedOrg: FrontendOrganization;
	setAssociatedOrg: React.Dispatch<
		React.SetStateAction<FrontendOrganization | undefined>
	>;

	associatedOrgClients: Array<FrontendClient>;
	setOrderSelectedClient: React.Dispatch<
		React.SetStateAction<FrontendClient | undefined>
	>;
	associatedOrgLicenses: Array<FrontendOrganizationLicense>;
	setAssociatedOrgLicenses: React.Dispatch<
		React.SetStateAction<Array<FrontendOrganizationLicense>>
	>;
	setOrderSelectedLicense: React.Dispatch<
		React.SetStateAction<FrontendOrganizationLicense | undefined>
	>;
	setConstructedFrontendOrder: React.Dispatch<
		React.SetStateAction<FrontendOrder | undefined>
	>;
	gotoNextPage: (
		client: FrontendClient,
		license: FrontendOrganizationLicense | undefined
	) => void;
}

export default function CPDetailsOrgAndUserComponent(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		testCategory,
		orderInfo,
		associatedOrg,
		associatedOrgClients,
		associatedOrgLicenses,
	} = props;

	const isOrgNameDifferent: boolean =
		associatedOrg.clientCompanyName != orderInfo.userInfo.clientCompanyName;

	const isOrgAddressDifferent: boolean =
		associatedOrg.address != orderInfo.userInfo.address ||
		associatedOrg.city != orderInfo.userInfo.city ||
		associatedOrg.province != orderInfo.userInfo.province ||
		associatedOrg.country != orderInfo.userInfo.country ||
		associatedOrg.postalCode != orderInfo.userInfo.postalCode;

	const isAnyDataDifferent: boolean =
		isOrgNameDifferent || isOrgAddressDifferent;

	const associatedClient: FrontendClient | undefined = findAssociatedClient(
		associatedOrgClients,
		orderInfo.userInfo.firstName,
		orderInfo.userInfo.lastName,
		orderInfo.userInfo.userName,
		orderInfo.userInfo.phoneNumber
	);

	const [selectedClient, setSelectedClient] = useState<
		FrontendClient | undefined
	>(associatedClient);
	const isNewClient: boolean = selectedClient ? false : true;

	const isClientNameDifferent: boolean = isNewClient
		? false
		: selectedClient?.firstName != orderInfo.userInfo.firstName ||
		  selectedClient?.lastName != orderInfo.userInfo.lastName;

	const isEmailDifferent: boolean = isNewClient
		? false
		: selectedClient?.email != orderInfo.userInfo.userName;
	const isPhoneNumberDifferent: boolean = isNewClient
		? false
		: selectedClient?.phoneNumber != orderInfo.userInfo.phoneNumber;

	const isAnyClientInfoDifferent: boolean =
		isClientNameDifferent || isEmailDifferent || isPhoneNumberDifferent;

	//license
	const associatedLicense: FrontendOrganizationLicense | undefined =
		findAssociatedLicense(
			associatedOrgLicenses,
			orderInfo.orderInfo.licenseNumber,
			orderInfo.orderInfo.licenseDesignation,
			orderInfo.orderInfo.licenseType
		);

	const [noLicense, setNoLicense] = useState<boolean>(
		orderInfo.orderInfo.licenseNumber ? false : true
	);
	const [selectedLicense, setSelectedLicense] = useState<
		FrontendOrganizationLicense | undefined
	>(associatedLicense);

	const isNewLicense: boolean = noLicense || selectedLicense ? false : true;

	const [orgDataHandleOption, setOrgDataHandleOption] =
		useState<DataHandleOption>(
			isAnyDataDifferent
				? DataHandleOption.USE_NEW
				: DataHandleOption.USE_IN_RECORD
		);

	const [clientDataHandleOption, setClientDataHandleOption] =
		useState<DataHandleOption>(
			isAnyClientInfoDifferent
				? DataHandleOption.USE_NEW
				: DataHandleOption.USE_IN_RECORD
		);

	const [loading, setLoading] = useState<boolean>(false);

	async function handleSubmit() {
		let shouldUpdateOrg: boolean =
			isAnyDataDifferent && orgDataHandleOption === DataHandleOption.USE_NEW;
		let shouldAddNewClient: boolean = isNewClient;
		let shouldUpdateClient: boolean =
			isAnyClientInfoDifferent &&
			clientDataHandleOption === DataHandleOption.USE_NEW;
		const shouldAddLicense: boolean = isNewLicense && !noLicense;

		if (
			!shouldUpdateOrg &&
			!shouldAddNewClient &&
			!shouldUpdateClient &&
			!shouldAddLicense
		) {
			props.setOrderSelectedClient(selectedClient as FrontendClient);
			props.setOrderSelectedLicense(selectedLicense);

			props.gotoNextPage(selectedClient as FrontendClient, selectedLicense);
			return;
		}
		try {
			setLoading(true);

			const updateOrgParams = shouldUpdateOrg
				? {
						clientCompanyName: orderInfo.userInfo.clientCompanyName,
						address: orderInfo.userInfo.address,
						city: orderInfo.userInfo.city,
						province: orderInfo.userInfo.province,
						country: orderInfo.userInfo.country,
						postalCode: orderInfo.userInfo.postalCode,
				  }
				: {};

			const clientInfo =
				shouldAddNewClient || shouldUpdateClient
					? {
							clientId: shouldUpdateClient
								? selectedClient?.clientId
								: undefined,
							firstName: orderInfo.userInfo.firstName,
							lastName: orderInfo.userInfo.lastName,
							email: orderInfo.userInfo.userName,
							phoneNumber: orderInfo.userInfo.phoneNumber,
					  }
					: {};
			const licenseInfo = shouldAddLicense
				? {
						licenseNumber: orderInfo.orderInfo.licenseNumber,
						licenseType: orderInfo.orderInfo.licenseType,
						licenseDesignation: orderInfo.orderInfo.licenseDesignation,
				  }
				: {};

			const response = await sendPOSTRequest(
				"/cp-register/update-org-and-client-info",
				{
					testCategoryId: testCategory.testCategoryId,
					shouldUpdateOrg,
					shouldAddNewClient,
					shouldUpdateClient,
					shouldAddLicense,
					organizationId: associatedOrg.organizationId,
					...updateOrgParams,
					...clientInfo,
					...licenseInfo,
				}
			);
			if (shouldUpdateOrg) {
				props.setAssociatedOrg((prevOrg) => {
					return {
						...(prevOrg as FrontendOrganization),
						clientCompanyName: orderInfo.userInfo.clientCompanyName,
						address: orderInfo.userInfo.address,
						city: orderInfo.userInfo.city,
						province: orderInfo.userInfo.province,
						country: orderInfo.userInfo.country,
						postalCode: orderInfo.userInfo.postalCode,
					};
				});
			}
			let client: FrontendClient | undefined;
			if (shouldAddNewClient || shouldUpdateClient) {
				client = response.data.updatedFrontendClient;
			} else {
				client = selectedClient;
			}

			props.setOrderSelectedClient(client);

			let license: FrontendOrganizationLicense | undefined;
			if (shouldAddLicense) {
				const newLicenseId: string = response.data.newLicenseId;
				const newLicense: FrontendOrganizationLicense = {
					licenseId: newLicenseId,
					licenseNumber: orderInfo.orderInfo.licenseNumber,
					licenseType: orderInfo.orderInfo.licenseType,
					licenseDesignation: orderInfo.orderInfo.licenseDesignation,
					inserted: new Date(),
				};
				props.setAssociatedOrgLicenses((prevLicenses) => [
					...prevLicenses,
					newLicense,
				]);
				license = newLicense;
			} else {
				license = selectedLicense;
			}
			props.setOrderSelectedLicense(license);

			openSnackbar(
				translation("ClientInformationUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			props.gotoNextPage(client as FrontendClient, license);
		} catch (err: any) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}
	const addressBlock: React.ReactNode = (
		<>
			<div>{`${associatedOrg.address}, `}</div>
			<div>{`${associatedOrg.city}, ${associatedOrg.province}, ${associatedOrg.country} `}</div>
			<div>{associatedOrg.postalCode}</div>
		</>
	);

	const newAddressBlock: React.ReactNode = (
		<>
			<div>{`${orderInfo.userInfo.address}, `}</div>
			<div>{`${orderInfo.userInfo.city}, ${orderInfo.userInfo.province}, ${orderInfo.userInfo.country} `}</div>
			<div>{orderInfo.userInfo.postalCode}</div>
		</>
	);

	const orgHeader: React.ReactNode = (
		<div>
			<div className="profile-icon-header-row typography-heading-03">
				<UserIcon
					strokeColor={NeutralColor.NEUTRAL_GREY_950}
					fillColor={NeutralColor.NEUTRAL_GREY_950}
				></UserIcon>
				{translation("OrganizationInformation")}
			</div>
			<div
				className="typography-caption"
				style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
			>
				{translation("OrganizationInformationDescription")}
			</div>
		</div>
	);

	const orgBody: React.ReactNode = (
		<div
			style={{
				marginTop: "10px",
				display: "flex",
				flexDirection: "column",
				gap: "5px",
			}}
		>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("AssociationId")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{associatedOrg.associationId}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("CompanyName")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{isOrgNameDifferent ? (
						<CPDifferentInfoRow
							dataHandleOption={orgDataHandleOption}
							oldData={associatedOrg.clientCompanyName}
							newData={orderInfo.userInfo.clientCompanyName}
						/>
					) : (
						associatedOrg.clientCompanyName
					)}
				</div>
			</div>

			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Address")}
				</div>
				<div
					className="overflow-ellipsis-text"
					style={{ maxWidth: "50%", textAlign: "right" }}
				>
					{isOrgAddressDifferent ? (
						<CPDifferentInfoRow
							dataHandleOption={orgDataHandleOption}
							oldData={addressBlock}
							newData={newAddressBlock}
						/>
					) : (
						addressBlock
					)}
				</div>
			</div>
		</div>
	);

	const handleOptions: Array<{
		key: string;
		name: string;
		description?: string;
		value: any;
	}> = Object.values(DataHandleOption).map((option: DataHandleOption) => {
		return {
			key: option,
			name: getDescriptionTextForHandleOption(option, translation),
			value: option,
		};
	});
	const handleOrgSwitch: React.ReactNode = isAnyDataDifferent ? (
		<StandardRadioGroup
			options={handleOptions}
			selectedOption={orgDataHandleOption}
			onChange={(e) => {
				setOrgDataHandleOption(e.target.value);
			}}
			className="data-handle-radio-group"
		/>
	) : null;

	//client info

	const clientHeader: React.ReactNode = (
		<div>
			<div className="profile-icon-header-row typography-heading-03">
				<UserIcon
					strokeColor={NeutralColor.NEUTRAL_GREY_950}
					fillColor={NeutralColor.NEUTRAL_GREY_950}
				></UserIcon>
				{translation("ClientInformation")}
			</div>
			<div
				className="typography-caption"
				style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
			>
				{translation("ClientInformationDescription")}
			</div>
		</div>
	);

	const selectClientOptions = associatedOrgClients.map(
		(client: FrontendClient) => {
			return {
				title: `${client.firstName || ""} ${client.lastName || ""} - ${
					client.email
				}`,
				value: client.clientId,
			};
		}
	);
	selectClientOptions.push({
		title: `--${translation("NewClient")}--`,
		value: "",
	});

	const selectClientDropdown: React.ReactNode = (
		<CustomDropdown
			id={"cp-select-client-dropdown"}
			label={translation("SelectClient")}
			options={selectClientOptions}
			value={selectedClient?.clientId || ""}
			setValue={(clientId: string) => {
				setSelectedClient(
					clientId
						? associatedOrgClients.find((client) => {
								return client.clientId == clientId;
						  })
						: undefined
				);
			}}
			displayEmpty
		></CustomDropdown>
	);

	const handleClientSwitch: React.ReactNode = isAnyClientInfoDifferent ? (
		<StandardRadioGroup
			options={handleOptions}
			selectedOption={clientDataHandleOption}
			onChange={(e) => {
				setClientDataHandleOption(e.target.value);
			}}
			className="data-handle-radio-group"
		/>
	) : null;

	const clientBody: React.ReactNode = (
		<div
			style={{
				marginTop: "10px",
				display: "flex",
				flexDirection: "column",
				gap: "5px",
			}}
		>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("ClientName")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{isClientNameDifferent ? (
						<CPDifferentInfoRow
							dataHandleOption={clientDataHandleOption}
							oldData={`${selectedClient?.firstName} ${selectedClient?.lastName}`}
							newData={`${orderInfo.userInfo.firstName} ${orderInfo.userInfo.lastName}`}
						/>
					) : (
						`${orderInfo.userInfo.firstName} ${orderInfo.userInfo.lastName}`
					)}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Email")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{isEmailDifferent ? (
						<CPDifferentInfoRow
							dataHandleOption={clientDataHandleOption}
							oldData={selectedClient?.email}
							newData={orderInfo.userInfo.userName}
						/>
					) : (
						orderInfo.userInfo.userName
					)}
				</div>
			</div>

			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("PhoneNumber")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{isPhoneNumberDifferent ? (
						<CPDifferentInfoRow
							dataHandleOption={clientDataHandleOption}
							oldData={selectedClient?.phoneNumber}
							newData={orderInfo.userInfo.phoneNumber}
						/>
					) : (
						orderInfo.userInfo.phoneNumber
					)}
				</div>
			</div>
		</div>
	);

	//license info

	const licenseHeader: React.ReactNode = (
		<div>
			<div className="profile-icon-header-row typography-heading-03">
				<UserIcon
					strokeColor={NeutralColor.NEUTRAL_GREY_950}
					fillColor={NeutralColor.NEUTRAL_GREY_950}
				></UserIcon>
				{translation("LicenseInformation")}
			</div>
			<div
				className="typography-caption"
				style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
			>
				{translation("LicenseInformationDescription")}
			</div>
		</div>
	);

	const selectLicenseOptions = associatedOrgLicenses.map(
		(license: FrontendOrganizationLicense) => {
			return {
				title: `${license.licenseNumber} ${
					license.licenseDesignation || ""
				} - ${license.licenseType}`,
				value: license.licenseId,
			};
		}
	);
	selectLicenseOptions.push({
		title: `${translation("NoLicense")}`,
		value: "None",
	});
	selectLicenseOptions.push({
		title: `--${translation("NewLicense")}--`,
		value: "",
	});

	const selectLicenseDropdown: React.ReactNode = (
		<CustomDropdown
			id={"cp-select-license-dropdown"}
			label={translation("SelectLicense")}
			options={selectLicenseOptions}
			value={noLicense ? "None" : selectedLicense?.licenseId || ""}
			setValue={(licenseId: string) => {
				if (licenseId == "None") {
					setNoLicense(true);
					setSelectedLicense(undefined);
				} else {
					setNoLicense(false);
					setSelectedLicense(
						licenseId
							? associatedOrgLicenses.find((license) => {
									return license.licenseId == licenseId;
							  })
							: undefined
					);
				}
			}}
			displayEmpty
		></CustomDropdown>
	);

	const licenseBody: React.ReactNode = noLicense ? null : (
		<div
			style={{
				marginTop: "10px",
				display: "flex",
				flexDirection: "column",
				gap: "5px",
			}}
		>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("LicenseNumber")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{orderInfo.orderInfo.licenseNumber}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("LicenseDesignation")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{orderInfo.orderInfo.licenseDesignation}
				</div>
			</div>

			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("LicenseType")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{orderInfo.orderInfo.licenseType}
				</div>
			</div>
		</div>
	);

	const confirmButton: React.ReactNode = (
		<GreenButton
			key="submit-client-change-button"
			id="submit-client-change-button"
			children={
				loading ? (
					<ButtonTextLoadingAnimation
						text={translation("Loading")}
					></ButtonTextLoadingAnimation>
				) : (
					translation("Next")
				)
			}
			disabled={loading}
			onClick={handleSubmit}
		></GreenButton>
	);

	const orgContent: React.ReactNode = (
		<div>
			<Card className="common-card card-normal-padding">
				{orgHeader}
				{orgBody}
				{handleOrgSwitch}
			</Card>
			<Card className="common-card card-normal-padding">
				{clientHeader}
				{selectClientDropdown}
				{clientBody}
				{handleClientSwitch}
			</Card>
			<Card className="common-card card-normal-padding">
				{licenseHeader}
				{selectLicenseDropdown}
				{licenseBody}
			</Card>
			{confirmButton}
		</div>
	);
	return orgContent;
}
